// Shopify sync health (N8): from each job's latest run and last success (job_status, migration 035), whether HHL Ops'
// copy of the store is fresh. Green: every job that has run succeeded within its window; amber: the last run failed,
// a success is late (twice its interval), or Shopify refused writes; red: a success is very late (four times its
// interval). The sidebar footer shows it, and both plans warn when the stock pull is behind (stockStale()), since the
// plan works from that stock. Plain functions, so tests check them (server/test/sync-health.test.ts).
import type { JobStatus } from "./db.gen.ts";

type Status = Pick<JobStatus, "job" | "last_ok" | "last_ok_at" | "last_error" | "last_finished_at">;

/** The jobs the store's freshness depends on, and how often each runs. */
export const WATCHED: Record<string, { label: string; everyHours: number }> = {
  stock: { label: "Stock", everyHours: 6 },
  catalog: { label: "Catalog", everyHours: 24 },
  sales: { label: "Sales", everyHours: 24 },
  "stock-history": { label: "Stock history", everyHours: 24 },
  forecast: { label: "Forecast", everyHours: 24 },
};

export type Level = "ok" | "warn" | "bad";
export type JobHealth = { job: string; label: string; level: Level; hoursAgo: number | null; text: string };
export type Health = { level: Level | "none"; text: string; jobs: JobHealth[] };

const HOUR = 3_600_000;

/** "3 days", "14 hours", "40 minutes". */
export function ageText(hours: number): string {
  if (hours >= 48) return `${Math.floor(hours / 24)} days`;
  if (hours >= 1) return `${Math.floor(hours)} hour${Math.floor(hours) === 1 ? "" : "s"}`;
  return `${Math.max(1, Math.round(hours * 60))} minutes`;
}

export function jobHealth(s: Status, now: number): JobHealth | null {
  const watch = WATCHED[s.job];
  if (!watch) return null;
  const hoursAgo = s.last_ok_at ? (now - Date.parse(s.last_ok_at)) / HOUR : null;
  const late = hoursAgo === null ? Number.POSITIVE_INFINITY : hoursAgo / watch.everyHours;
  const failed = s.last_ok === false;
  const level: Level = late > 4 ? "bad" : failed || late > 2 ? "warn" : "ok";
  const when = hoursAgo === null ? "never pulled" : `last pulled ${ageText(hoursAgo)} ago`;
  const text = `${watch.label} ${when}${failed ? ` (the last try failed${s.last_error ? `: ${s.last_error}` : ""})` : ""}`;
  return { job: s.job, label: watch.label, level, hoursAgo, text };
}

/** The store's health over every watched job that has ever run; "none" when none has (Shopify isn't set up). */
export function syncHealth(rows: readonly Status[], failedWrites: number, now: number): Health {
  const jobs = rows.flatMap((s) => jobHealth(s, now) ?? []);
  if (!jobs.length) return { level: "none", text: "", jobs };
  const worst = jobs.reduce<JobHealth | null>((w, j) => (rank(j.level) > rank(w?.level ?? "ok") ? j : w), null);
  const level: Level = worst?.level === "bad" ? "bad" : worst || failedWrites ? "warn" : "ok";
  const text = worst ? worst.text : failedWrites ? `${failedWrites} change${failedWrites === 1 ? "" : "s"} Shopify didn't take` : "Shopify in sync";
  return { level, text, jobs };
}

const rank = (l: Level) => (l === "bad" ? 2 : l === "warn" ? 1 : 0);

/**
 * The stock pull, when the stock the plans work from is old (no success for over twice its interval, 12 hours): the
 * plans' warning. A failed run soon after a success turns the sidebar amber but isn't this: the stock is still fresh.
 */
export function stockStale(rows: readonly Status[], now: number): JobHealth | null {
  const s = rows.find((r) => r.job === "stock");
  const h = s ? jobHealth(s, now) : null;
  return h && (h.hoursAgo === null || h.hoursAgo > 2 * WATCHED.stock.everyHours) ? h : null;
}
